import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import bind from '../bind';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { increment } from '../elog';
import windowWrapper from '../window_wrapper';

import Modal from './modal';
import SigninForm from './signin_form';
import SignupForm from './signup_form';

import { withUserContext, IUserContext } from './user_context_provider';
import { isEppoExpEnabled } from '../eppo/eppoExperimentHelpers';
import { withEppoClientContext, IEppoClientContext } from './eppo/EppoClientContextProvider';
import eppoFlags from '../eppoFlags';
import experiments from '../experiments';
import { Paths } from '../url_helpers';
import { isExperimentEnabled } from '../user_context_helpers';

const COMPONENT_NAME = 'SignupSigninModal';

interface IProps {
  redirectTo?: string;
  message?: string;
  messageComponent?: JSX.Element;
  active?: boolean;
  onRequestClose: Function;
  trackSource: string;
  onlyShowMessageOnTab?: 'signin' | 'signup';
  showSignInOnOpen?: boolean;
  variant?: 'default' | 'continue';
  prioritizeOauth?: boolean;
  window?: Window;

  // For the modal deprecation experiment, we only want to redirect
  // to the signup page if the user clicks the "Sign Up" link in the header.
  //
  // This prop allows for a manual override for that one use case in the
  // treatment group without changing the behavior of the control group.
  redirectToSignUpPage?: boolean;
}

interface State {
  showSignup: boolean;
}

export function signupOrSigninUrl(showSignup: boolean, redirectTo: string, source: string): string {
  if (showSignup) {
    return Paths.signUp.expand({ redirect_to: redirectTo, redirect_source: source });
  }

  return Paths.signIn.expand({ redirect_to: redirectTo, redirect_source: source });
}


export type CombinedProps = IProps & IUserContext & IEppoClientContext;

export class SignupSigninModal extends React.Component<CombinedProps, State> {
  static defaultProps: Partial<IProps> = {
    redirectTo: window.location.href,
    variant: 'default',
    window: windowWrapper,
  };

  constructor(props) {
    super(props);

    this.state = {
      showSignup: !props.showSignInOnOpen,
    };
  }

  @bind
  switchToSignup() {
    this.setState({ showSignup: true });
  }

  @bind
  switchToLogin() {
    this.setState({ showSignup: false });
  }

  componentDidMount() {
    if (this.props.active) {
      this.trackView();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.active && !prevProps.active) {
      this.trackView();
      this.setState({ showSignup: !this.props.showSignInOnOpen });
    }
  }

  shouldRedirect() {
    return isEppoExpEnabled({
      flagKey: eppoFlags.SIGNUP_FORM_REDIRECTS,
      eppoClient: this.props.eppoClient,
      user: this.props.user,
    });
  }

  trackView() {
    const view = this.state.showSignup ? 'signup' : 'signin';

    increment({
      name: `signup_signin_modal.view.${view}`,
      tags: [`pathname:${this.props.window.location.pathname}`, `tracksource:${this.props.trackSource}`],
    });

    // if user is in SIGNUP_FORM_REDIRECTS experiment and server side view
    // tracking is enabled, don't track mparticle event from this component
    if (isExperimentEnabled(this.props.user, experiments.CORE_SIGNUP_SIGNIN_VIEW_TRACKING) && this.shouldRedirect()) return;

    trackEvent({
      view,
      source: this.props.trackSource,
      eventName: MParticleEventName.ViewedSignupSignin,
      componentName: COMPONENT_NAME,
    });
  }

  renderForm() {
    const { redirectTo } = this.props;

    if (this.state.showSignup) {
      return (
        <SignupForm
          redirectTo={redirectTo}
          shouldRenderHeader={this.shouldRenderHeader()}
          variant={this.props.variant}
          prioritizeOauth={this.props.prioritizeOauth}
          switchToSignin={this.switchToLogin}
        />
      );
    }

    return (
      <SigninForm
        redirectTo={redirectTo}
        shouldRenderHeader={this.shouldRenderHeader()}
        variant={this.props.variant}
        prioritizeOauth={this.props.prioritizeOauth}
        switchToSignup={this.switchToSignup}
      />
    );
  }

  shouldRenderHeader() {
    return (!this.props.messageComponent && !this.props.message) || this.props.prioritizeOauth;
  }

  renderMessage() {
    if ((this.props.onlyShowMessageOnTab == 'signin' && this.state.showSignup) || (this.props.onlyShowMessageOnTab == 'signup' && !this.state.showSignup)) {
      return null;
    }

    if (this.props.messageComponent) {
      return this.props.messageComponent;
    }

    if (this.props.message) {
      return (
        <div className="scaling-mb-4">
          <RCAlertBox type="info" small>
            {this.props.message}
          </RCAlertBox>
        </div>
      );
    }

    return null;
  }

  render() {
    if (this.props.active && this.shouldRedirect()) {
      this.props.window.location.assign(
        signupOrSigninUrl(
          this.props.redirectToSignUpPage,
          this.props.redirectTo,
          this.props.trackSource,
        ),
      );

      return null;
    }

    return (
      <Modal
        isOpen={this.props.active}
        onRequestClose={this.props.onRequestClose}
        subClass="signup-signin"
        headerMarkup={(
          <div className="signup-signin__tabs">
            <button
              className={
                classNames('signup-signin__tab', {
                  'signup-signin__tab--active': this.state.showSignup,
                })
              }
              onClick={this.switchToSignup}
              type="button"
            >
              {I18n.t('commons.signupForm.signUp')}
            </button>
            <button
              className={
                classNames('signup-signin__tab', {
                  'signup-signin__tab--active': !this.state.showSignup,
                })
              }
              onClick={this.switchToLogin}
              type="button"
            >
              {I18n.t('commons.signupForm.logIn')}
            </button>
          </div>
        )}
      >
        <div className="signup-signin">
          <div className="signup-signin__form">
            {this.renderMessage()}
            {this.renderForm()}
          </div>
        </div>
      </Modal>
    );
  }
}

export default withUserContext(withEppoClientContext(SignupSigninModal));
